// NB: This file is allowed to import 'shared/verifications/checks.json' directly.
// eslint-disable-next-line no-restricted-imports
import checkProperties from 'shared/verifications/checks.json';

import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import { ToggleOption } from 'dashboard/models/document';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import {
  IdCompareOption,
  VerificationTemplateVersion,
} from 'dashboard/models/verification-template-version';
import { CountryCode } from 'lib/constants/locales';
import { CheckName } from 'lib/verificationCheck';

export enum CheckReasonStatus {
  NotApplicable = 'not_applicable',
  Deprecated = 'deprecated',
  Passed = 'passed',
  Failed = 'failed',
  Pending = 'pending',
}

export type CheckProperties = Record<
  CheckName,
  Readonly<{
    readonly dependent_features: string[];
    readonly lifecycle_stage: LifecycleStage;
    readonly reasons: Record<
      string,
      Readonly<{ default_status: CheckReasonStatus; frontend_visibility: boolean }>
    >;
  }>
>;

// Use this constant when working with 'shared/verifications/checks.json' as it is typed
export const CHECK_PROPERTIES = checkProperties as CheckProperties;

// TODO(billz/jonny): Move the Risk Config and Match Requirement check configs to their own files in front-end/dashboard/models/verification-check-config/.
export enum AugmentationMethodType {
  Tokenize = 'tokenize',
  ReplaceCharacterSets = 'replace_character_sets',
}

// TIP: Add these to CONFIGURABLE_NORMALIZATION_METHOD_TYPES in front-end/dashboard/components-lib/MatchRequirementPanel/NormalizationMethodSection.tsx
// if you want them to be editable in the VTE/FLE.
export enum NormalizationMethodType {
  AbbreviateStreetSuffix = 'abbreviate_street_suffix',
  AbbreviateStreetUnit = 'abbreviate_street_unit',
  AbbreviateSubdivision = 'abbreviate_subdivision',
  CanonicalizeEmailAddress = 'canonicalize_email_address',
  ExpandCityAbbreviation = 'expand_city_abbreviation',
  ExpandCitySuffix = 'expand_city_suffix',
  ExpandStreetSuffix = 'expand_street_suffix',
  ExpandStreetUnit = 'expand_street_unit',
  ExpandSubdivision = 'expand_subdivision',
  ExtractBusinessName = 'extract_business_name',
  ExtractLetters = 'extract_letters',
  ExtractNumbers = 'extract_numbers',
  ExtractRegistrationNumber = 'extract_registration_number',
  ExtractStreet = 'extract_street',
  ExtractStreetName = 'extract_street_name',
  ExtractStreetNumber = 'extract_street_number',
  ExtractStreetType = 'extract_street_type',
  ExtractStreetUnit = 'extract_street_unit',
  ExtractSubstring = 'extract_substring',
  FilterCharacters = 'filter_characters',
  FoldCharacters = 'fold_characters',
  LowercaseLetters = 'lowercase_letters',
  NormalizeCjkFullwidthCharacters = 'normalize_cjk_fullwidth_characters',
  RemoveBusinessNameSuffix = 'remove_business_name_suffix',
  RemoveCharacters = 'remove_characters',
  RemoveLetters = 'remove_letters',
  RemoveNumbers = 'remove_numbers',
  RemovePrefixes = 'remove_prefixes',
  RemoveSpecialCharacters = 'remove_special_characters',
  RemoveStreetSuffix = 'remove_street_suffix',
  RemoveSuffixes = 'remove_suffixes',
  RemoveWhitespaces = 'remove_whitespaces',
  ReplaceCharacters = 'replace_characters',
  Titleize = 'titleize',
  Tokenize = 'tokenize',
  Transliterate = 'transliterate',
  UppercaseLetters = 'uppercase_letters',
}

export enum ComparisonMethodType {
  Address = 'address',
  Ai = 'ai',
  DateDifferenceDay = 'date_difference_day',
  DateDifferenceMonth = 'date_difference_month',
  DateDifferenceYear = 'date_difference_year',
  DateSimilarity = 'date_similarity',
  DoingBusinessAs = 'doing_business_as',
  HaversineDistance = 'haversine_distance',
  LogicComposition = 'logic_composition',
  Nickname = 'nickname',
  StringDifference = 'string_difference',
  StringSimilarity = 'string_similarity',
  Substring = 'substring',
  Tokenization = 'tokenization',

  DamerauLevenshteinSimilarity = 'damerau_levenshtein_similarity',
  JaroWinklerSimilarity = 'jaro_winkler_similarity',
  StringDifferencePrefix = 'string_difference_prefix',
  StringDifferenceSuffix = 'string_difference_suffix',
}

// Refer to go/check-lifecycle for more details about the definition of each stage.
export enum LifecycleStage {
  Alpha = 'alpha',
  Beta = 'beta',
  Deprecated = 'deprecated',
  Sunset = 'sunset',
}

export interface MatchRequirement {
  'attribute-type'?: string;
  'attributes-to-compare': ComparedAttribute[];
  'comparison-methods': ComparisonMethod[];
  description?: string; // The representation of the check requirement using words, i.e First Name
  name?: string; // A key to access the check requirement, i.e first_name
  'match-level-minimum'?: string; // When we have a match requirement that's from a match vendor as opposed to an identity vendor
  'normalization-methods': NormalizationMethod[];
}

interface MatchGroupBase {
  name: string;
  description: string;
  'match-requirements': MatchRequirement[];
}

export interface SingleCountryMatchGroup extends MatchGroupBase {
  'country-code': CountryCode | 'default';
}

export interface MultipleCountryMatchGroup extends MatchGroupBase {
  'country-codes': CountryCode[] | ['default'];
}

export type MatchGroup = MultipleCountryMatchGroup | SingleCountryMatchGroup;

export interface RequiredExtraction {
  'field-name': string;
  'accept-condition': any;
  // deprecated
  name: string;
}

// enumerized values from InquiryV2::FieldKey, matches check server models
export enum ComparedAttribute {
  NameFirst = 'name_first',
  NameMiddle = 'name_middle',
  NameLast = 'name_last',
  AddressStreet1 = 'address_street_1',
  AddressStreet2 = 'address_street_2',
  AddressCity = 'address_city',
  AddressSubdivision = 'address_subdivision',
  AddressPostalCode = 'address_postal_code',
  AddressCountryCode = 'address_country_code',
  Birthdate = 'birthdate',
  IdentificationNumber = 'identification_number',
  IdentificationClass = 'identification_class',
  PhoneNumber = 'phone_number',
  EmailAddress = 'email_address',
  SocialSecurityNumber = 'social_security_number',
  // TODO (tianen): these attributes are for check requirements for GovernmentId/ExtractionInconsistencyDetection
  // may want to seperate these out into separate these check requirements / compared attributes to a separate interface / model
  Eyes = 'eyes',
  Sex = 'sex',
  Height = 'height',
  Weight = 'weight',
  IssuingCountry = 'issuing_country',
  IssueDate = 'issue_date',
  ExpirationDate = 'expiration_date',
}

export interface RiskData {
  category?: RiskCategory;
  reason?: string;
  score?: number;
  level?: RiskLevel;
  triggered?: boolean;
}

export interface RiskConfig {
  category: RiskCategory;
  reason: string;
  enabled: boolean;
  'threshold-high': number;
  'threshold-medium': number;
  'threshold-low': number;
  'trigger-level': RiskLevel;
}

// Refer to app/lib/verification_risk_config.rb.
export enum RiskCategory {
  AgeComparison = 'age_comparison',
  AgeThresholdComparison = 'age_threshold_comparison',
  FaceComparison = 'face_comparison',
  ImageColorVariance = 'image_color_variance',
  PortraitClarity = 'portrait_clarity',
  PublicFigureDetection = 'public_figure_detection',

  // DEPRECATED
  ImageColorfulness = 'image_colorfulness',
}

export enum RiskLevel {
  Minimal = 'minimal',
  Low = 'low',
  Medium = 'medium',
  High = 'high',
}

export const RiskLevelValue = {
  [RiskLevel.Minimal]: 0,
  [RiskLevel.Low]: 1,
  [RiskLevel.Medium]: 2,
  [RiskLevel.High]: 3,
};

// should be broken out to separate interfaces, but contains all attributes for now
export interface ComparisonMethod {
  'full-match-threshold'?: number;
  'ignore-white-spaces'?: boolean;
  length?: number;
  'match-level-minimum': string;
  'partial-match-threshold'?: number;
  'max-char-difference'?: number;
  'token-match-count-minimum'?: number;
  'attribute-length-minimum'?: number;
  // for ai comparison method
  model?: string;
  type: ComparisonMethodType;
}

export interface AugmentationMethod {
  type: AugmentationMethodType;
}

// should be broken out to separate interfaces, but contains all attributes for now
export interface NormalizationMethod {
  type: NormalizationMethodType;
  // for tokenize normalization method
  'token-delimiter-regex'?: string;
}

interface CharacterSet {
  characters: string[];
  replacement: string;
  'case-insensitive'?: boolean;
}

// should be broken out to separate interfaces, but contains all attributes for now
export interface AugmentationMethod {
  type: AugmentationMethodType;
  // for tokenize normalization method
  'token-delimiter-regex'?: string;
  // for replace characters method
  'character-sets': CharacterSet[];
}

export interface ComparisonInputsConfig {
  slug: string;
  'match-requirements': MatchRequirement[];
}

export type AttributeComparisonCheckName =
  | CheckName.IdAttributeComparison
  | CheckName.DatabaseAttributeComparison
  | CheckName.SelfieAttributeComparison;

export interface AttributeComparisonCheckConfig extends VerificationCheckConfig {
  check: AttributeComparisonCheckName;
  'comparison-inputs-config'?: ComparisonInputsConfig[];
}

export function isAttributeComparisonCheck(
  verificationCheck: VerificationCheckConfig,
): verificationCheck is AttributeComparisonCheckConfig {
  switch (verificationCheck.check) {
    case CheckName.IdAttributeComparison:
    case CheckName.DatabaseAttributeComparison:
    case CheckName.SelfieAttributeComparison:
      return true;
    default:
      return false;
  }
}

/**
 * Returns all unique attributes referenced in the match requirements of the attribute_comparison
 * check configured by the input verification template version in a Set, for example:
 * new Set('name_first', 'name_last', 'birthdate').
 *
 * If attribute_comparison is not configured, returns an empty Set.
 */
export function getAttributesToCompare(
  verificationTemplateVersion: VerificationTemplateVersion,
): Set<string> {
  const verificationCheckConfigs = verificationTemplateVersion.attributes['verification-checks'];
  const attributeComparisonCheckConfig = verificationCheckConfigs.find((check) =>
    isAttributeComparisonCheck(check),
  );

  if (
    !attributeComparisonCheckConfig ||
    attributeComparisonCheckConfig['toggle-option'] === ToggleOption.Disabled
  ) {
    return new Set();
  }

  // the attributes to compare are not configurable for selfies
  if (attributeComparisonCheckConfig['check'] === CheckName.SelfieAttributeComparison) {
    return new Set(['selfies']);
  }

  const comparisonInputsConfig =
    (attributeComparisonCheckConfig as AttributeComparisonCheckConfig)[
      'comparison-inputs-config'
    ] || [];
  return new Set(
    comparisonInputsConfig.flatMap((config) => {
      return config['match-requirements'].flatMap((requirement) => {
        return requirement['attributes-to-compare'];
      });
    }),
  );
}

export type IdCompareCheckName =
  | CheckName.SelfieAgeComparison
  | CheckName.SelfieAgeInconsistencyDetection
  | CheckName.SelfieIdComparison;

export interface IdCompareCheckConfig extends VerificationCheckConfig {
  check: IdCompareCheckName;
  'id-compare-option': IdCompareOption;
}

export function isIdCompareCheck(
  verificationCheck: VerificationCheckConfig,
): verificationCheck is IdCompareCheckConfig {
  switch (verificationCheck.check) {
    case CheckName.SelfieAgeComparison:
    case CheckName.SelfieAgeInconsistencyDetection:
    case CheckName.SelfieIdComparison:
      return true;
    default:
      return false;
  }
}

export type MatchRequirementsCheckName =
  | CheckName.DatabaseIdentityComparison
  | CheckName.DatabaseInquiryComparison
  | CheckName.IdAccountComparison
  | CheckName.IdExtractionInconsistencyDetection
  | CheckName.IdInquiryComparison
  | CheckName.DocumentExtractedPropertiesDetection;

export interface MatchRequirementsCheckConfig extends VerificationCheckConfig {
  check: MatchRequirementsCheckName;
  'check-requirements': MatchRequirement[];
}

export type DefaultMatchRequirementMapping =
  | Record<MatchRequirementsCheckName, (MatchRequirement | RequiredExtraction)[]>
  | Record<string, never>;

// Reference: https://www.notion.so/withpersona/Check-Requirements-c4b8cee9ebf04fe7b6bc7bb018aa57aa
export function isMatchRequirementsCheck(
  verificationCheck: VerificationCheckConfig,
): verificationCheck is MatchRequirementsCheckConfig {
  switch (verificationCheck.check) {
    case CheckName.DatabaseBusinessIdentityComparison:
    case CheckName.DatabaseStandardIdentityComparison:
    case CheckName.DatabaseIdentityComparison:
    case CheckName.DatabaseInquiryComparison:
    case CheckName.IdAccountComparison:
    case CheckName.IdExtractionInconsistencyDetection:
    case CheckName.IdInquiryComparison:
    case CheckName.IdNfcAttributeComparison:
      return true;
    default:
      return false;
  }
}

export type MatchGroupsCheckName =
  | CheckName.DatabaseStandardIdentityComparison
  | CheckName.DatabaseIdentityComparison;

export interface MatchGroupsCheckConfig extends VerificationCheckConfig {
  check: MatchGroupsCheckName;
  'match-groups': MatchGroup[];
}

export function isMatchGroupCheck(
  verificationCheck: VerificationCheckConfig,
): verificationCheck is MatchGroupsCheckConfig {
  switch (verificationCheck.check) {
    case CheckName.DatabaseBusinessIdentityComparison:
    case CheckName.DatabaseStandardIdentityComparison:
      return true;
    // Now that we have a period where we'll support both for DBV, we'll want to support both
    case CheckName.DatabaseIdentityComparison:
      if (isMatchRequirementsCheck(verificationCheck))
        return verificationCheck['check-requirements']?.length === 0;
      return false;
    default:
      return false;
  }
}

export function isMatchGroupListCheck(
  verificationCheck: VerificationCheckConfig,
): verificationCheck is MatchRequirementsCheckConfig {
  switch (verificationCheck.check) {
    // Only business identity comparison has a string input for country codes, all others are lists
    case CheckName.DatabaseBusinessIdentityComparison:
      return false;
    default:
      return true;
  }
}

export function isDeprecatedCheckReason(checkName: CheckName, reasonName: string): boolean {
  const reason = CHECK_PROPERTIES[checkName]['reasons'][reasonName];

  if (reason) {
    return (reason['default_status'] as CheckReasonStatus) === CheckReasonStatus.Deprecated;
  }

  return false;
}

// Refer to go/check-lifecycle for more details about the definition of each stage.
export function checkLifecycleStage(checkName: CheckName): LifecycleStage {
  return CHECK_PROPERTIES[checkName]['lifecycle_stage'];
}

export function isAlphaCheck(checkName: CheckName): boolean {
  return checkLifecycleStage(checkName) === 'alpha';
}

export function isBetaCheck(checkName: CheckName): boolean {
  return checkLifecycleStage(checkName) === 'beta';
}

export function isDeprecatedCheck(checkName: CheckName): boolean {
  return checkLifecycleStage(checkName) === 'deprecated';
}

export function isSunsetCheck(checkName: CheckName): boolean {
  return checkLifecycleStage(checkName) === 'sunset';
}

export function isLegacyInternationalDatabaseCheck(
  existingChecks: VerificationCheckConfig[],
  check: CheckName,
): boolean {
  // When there are match groups, these will take precedence over legacy checks.
  // Ensure the the overall set of checks includes a DBV that leverages match groups
  // and that the specific check in question is a legacy international database check
  return (
    (
      existingChecks.find(
        (existingCheck: VerificationCheckConfig) =>
          existingCheck.check === CheckName.DatabaseIdentityComparison,
      ) as MatchGroupsCheckConfig
    )?.['match-groups']?.length > 0 && checkLifecycleStage(check) === 'sunset'
  );
}

export type RiskConfigCheckName =
  | CheckName.IdSelfieComparison
  | CheckName.SelfieAgeThresholdComparison
  | CheckName.SelfieIdComparison;

export interface RiskConfigCheckConfig extends VerificationCheckConfig {
  check: RiskConfigCheckName;
  'risk-configs': RiskConfig[];
}

export function isRiskConfigCheck(
  verificationCheck: VerificationCheckConfig,
): verificationCheck is RiskConfigCheckConfig {
  switch (verificationCheck.check) {
    case CheckName.IdSelfieComparison:
    case CheckName.SelfieIdComparison:
      return true;
    default:
      return false;
  }
}

// Check access doc: https://www.notion.so/withpersona/Resource-PLG-upsell-motion-Product-Features-and-what-they-get-you-2fe4726d534e43e4bf779c24208737c2#4514553b06ed4ce492dc85ca308141f2
export const STARTER_DISABLED_CHECKS = [
  // Gov Id Verification checks:
  CheckName.IdAccountComparison,
  CheckName.IdAgeComparison,
  CheckName.IdBarcodeDetection,
  CheckName.IdBarcodeInconsistencyDetection,
  CheckName.IdColorInconsistencyDetection,
  CheckName.IdDoubleSideDetection,
  CheckName.IdElectronicReplicaDetection,
  CheckName.IdExtractedPropertiesDetection,
  CheckName.IdExtractionInconsistencyDetection,
  CheckName.IdInconsistentRepeatDetection,
  CheckName.IdInquiryComparison,
  CheckName.IdPaperDetection,
  CheckName.IdRealIdDetection,
  CheckName.IdRepeatDetection,

  // Selfie Verification checks:
  CheckName.SelfieAccountComparison,
  CheckName.SelfieAgeComparison,
  CheckName.SelfieAgeInconsistencyDetection,
  CheckName.SelfieAgeThresholdComparison,

  // Database Verification checks:
  CheckName.DatabaseTwoPlusTwoDetection,
  CheckName.DatabaseAddressComparison,
  CheckName.DatabaseBirthdateComparison,
  CheckName.DatabaseSocialSecurityNumberComparison,
  CheckName.DatabaseNameComparison,
  CheckName.DatabasePhoneNumberComparison,

  // Document Verifications checks:
  CheckName.DocumentColorModeDetection,
  CheckName.DocumentCompromisedDetection,
  CheckName.DocumentDigitalTextModificationDetection,
  CheckName.DocumentExpiredDetection,
  CheckName.DocumentExtractedPropertiesDetection,
  CheckName.DocumentFabricationDetection,
  CheckName.DocumentHandwritingDetection,
  CheckName.DocumentImageCopyMoveDetection,
  CheckName.DocumentImageInconsistentTimestampDetection,
  CheckName.DocumentImageSuspiciousMetadataDetection,
  CheckName.DocumentJpegOriginalImageDetection,
  CheckName.DocumentPdfAbnormalFontDetection,
  CheckName.DocumentPdfAnnotationDetection,
  CheckName.DocumentPdfContentTypeDetection,
  CheckName.DocumentPdfEditorDetection,
  CheckName.DocumentPdfInconsistentTimestampDetection,
  CheckName.DocumentPdfModificationDetection,
  CheckName.DocumentPortraitDetection,
  CheckName.DocumentSelfieComparison,
  CheckName.DocumentTamperDetection,
  CheckName.DocumentTemplateDetection,
  CheckName.DocumentTypeDetection,
  CheckName.DocumentUnprocessableSubmissionDetection,
];

export const BASIC_DISABLED_CHECKS = [
  // Database Verification checks:
  CheckName.DatabaseTwoPlusTwoDetection,
  CheckName.DatabaseAddressComparison,
  CheckName.DatabaseBirthdateComparison,
  CheckName.DatabaseSocialSecurityNumberComparison,
  CheckName.DatabaseNameComparison,
  CheckName.DatabasePhoneNumberComparison,

  // Document Verifications checks:
  CheckName.DocumentColorModeDetection,
  CheckName.DocumentCompromisedDetection,
  CheckName.DocumentDigitalTextModificationDetection,
  CheckName.DocumentExtractedPropertiesDetection,
  CheckName.DocumentFabricationDetection,
  CheckName.DocumentHandwritingDetection,
  CheckName.DocumentImageCopyMoveDetection,
  CheckName.DocumentImageInconsistentTimestampDetection,
  CheckName.DocumentImageSuspiciousMetadataDetection,
  CheckName.DocumentJpegOriginalImageDetection,
  CheckName.DocumentPdfAbnormalFontDetection,
  CheckName.DocumentPdfAnnotationDetection,
  CheckName.DocumentPdfContentTypeDetection,
  CheckName.DocumentPdfEditorDetection,
  CheckName.DocumentPdfInconsistentTimestampDetection,
  CheckName.DocumentPdfModificationDetection,
  CheckName.DocumentPortraitDetection,
  CheckName.DocumentSelfieComparison,
  CheckName.DocumentTamperDetection,
  CheckName.DocumentTemplateDetection,
  CheckName.DocumentTypeDetection,
  CheckName.DocumentUnprocessableSubmissionDetection,
];

export const ITE_OMITTED_CHECKS = [
  // Hiding AAMVA db lookup check in editor for now, as current best practice is to run a separate AAMVA verification
  CheckName.IdAamvaDatabaseLookup,
  // Hide document fabrication detection in ITE to preven users from configuring the check.
  // The check produces unreliable results
  CheckName.DocumentFabricationDetection,
  CheckName.DocumentHandwritingDetection,
  // Pdf modification is deprecated in favour of Pdf editor and Pdf inconsistent timestamp checks
  CheckName.DocumentPdfModificationDetection,
  // TODO: [Documents] Un-hide biometrics checks after Documents figures out Biometrics requirements
  CheckName.DocumentPortraitDetection,
  CheckName.DocumentSelfieComparison,
  // This is a check that should always be ON by default and is used as to gracefully catch errors from unprocessable submissions,
  // we hide it as the builder does not need to configure this check at all
  CheckName.DocumentUnprocessableSubmissionDetection,
];

/**
 * helper method to gate enabled checks in InquiryTemplateConfigEditor by inquiries product feature.
 */

export const checkEnabledByTier = (check: CheckName, accessTier: EditorAccessTier) => {
  switch (accessTier) {
    case EditorAccessTier.InquiriesFree:
      return !STARTER_DISABLED_CHECKS.includes(check);
    case EditorAccessTier.InquiriesBasic:
      return !BASIC_DISABLED_CHECKS.includes(check);
    case EditorAccessTier.InquiriesEnhanced:
      return true;
  }
};
